import { useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { Paginacao } from "../components/common/Paginacao.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FilterField,
  FilterPanel,
} from "../components/common/FilterBar.js";

const PAGE_SIZE = 30;

export default function Audit() {
  const { user } = useAuth();
  const t = useT();
  const adminCompanyIds = user
    ? user.isSuperAdmin
      ? user.allowedCompanyIds
      : user.memberships.filter((m) => m.active && m.role === "admin").map((m) => m.companyId)
    : [];

  const [companyId, setCompanyId] = useState<number | null>(adminCompanyIds[0] ?? null);
  const [entityKindFilter, setEntityKindFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [offset, setOffset] = useState(0);

  const companiesQuery = trpc.companies.list.useQuery();
  const adminCompanies = (companiesQuery.data ?? []).filter((c) => adminCompanyIds.includes(c.id));

  const filters = {
    entityKind: entityKindFilter || undefined,
    action: actionFilter || undefined,
    limit: PAGE_SIZE,
    offset,
  };

  const auditQuery = trpc.audit.listByCompany.useQuery(
    { companyId: companyId ?? 0, ...filters },
    { enabled: companyId !== null },
  );

  if (!user) return null;

  if (adminCompanies.length === 0) {
    return (
      <Shell title={t("audit.title")}>
        <p className="text-sm text-neutral-400">{t("audit.notAdmin")}</p>
      </Shell>
    );
  }

  const data = auditQuery.data;
  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;

  return (
    <Shell title={t("audit.title")}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 grow basis-full sm:basis-0">
          <FilterPanel testId="audit-filter-panel">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <FilterField label={t("audit.filterCompany")}>
                <select
                  value={companyId ?? ""}
                  onChange={(e) => {
                    setCompanyId(Number(e.target.value));
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-auto"}
                >
                  {adminCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("audit.filterEntity")}>
                <input
                  value={entityKindFilter}
                  onChange={(e) => {
                    setEntityKindFilter(e.target.value);
                    setOffset(0);
                  }}
                  list="audit-entity-kinds"
                  placeholder={t("audit.entityKindPlaceholder")}
                  className={FILTER_INPUT + " w-full sm:w-52"}
                />
                <datalist id="audit-entity-kinds">
                  <option value="customer" />
                  <option value="vehicle" />
                  <option value="company_membership" />
                  <option value="user_invite" />
                  <option value="attachment" />
                  <option value="app_user" />
                  <option value="company" />
                </datalist>
              </FilterField>
              <FilterField label={t("audit.filterAction")}>
                <input
                  value={actionFilter}
                  onChange={(e) => {
                    setActionFilter(e.target.value);
                    setOffset(0);
                  }}
                  list="audit-actions"
                  placeholder={t("audit.actionPlaceholder")}
                  className={FILTER_INPUT + " w-full sm:w-44"}
                />
                <datalist id="audit-actions">
                  <option value="create" />
                  <option value="update" />
                  <option value="delete" />
                  <option value="login" />
                  <option value="logout" />
                  <option value="set_role" />
                  <option value="deactivate" />
                  <option value="reactivate" />
                  <option value="accept" />
                  <option value="revoke" />
                  <option value="resend" />
                  <option value="upload" />
                  <option value="password_reset_request" />
                  <option value="password_reset_confirm" />
                  <option value="change_password" />
                </datalist>
              </FilterField>
            </div>
          </FilterPanel>
        </div>
        <div className="text-xs text-neutral-500">
          {showing > 0 && (
            <>
              {offset + 1}–{offset + showing} {t("audit.of")} {total}
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {auditQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("audit.loading")}</p>
        )}
        {auditQuery.error && <p className="p-4 text-sm text-red-400">{auditQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("audit.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-44">{t("audit.colWhen")}</th>
                <th className="text-left px-3 py-2 w-36">{t("audit.colWho")}</th>
                <th className="text-left px-3 py-2 w-32">{t("audit.colEntity")}</th>
                <th className="text-left px-3 py-2 w-12">ID</th>
                <th className="text-left px-3 py-2 w-32">{t("audit.colAction")}</th>
                <th className="text-left px-3 py-2">{t("audit.colChanges")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((row) => (
                <tr key={row.id} className="align-top">
                  <td className="px-3 py-2 text-xs text-neutral-400 whitespace-nowrap">
                    {new Date(row.occurredAt).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {row.impersonatorName ? (
                      // F-impersonate-audit — "Landione (como Volodymyr)": quem
                      // apertou o botão primeiro, o usuário emprestado depois.
                      <>
                        {row.impersonatorName}{" "}
                        <span className="text-neutral-500">
                          ({t("audit.asUserPre")} {row.userName ?? "—"})
                        </span>
                      </>
                    ) : (
                      (row.userName ?? <span className="text-neutral-600">—</span>)
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-300">{row.entityKind}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500">{row.entityId ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="inline-block rounded bg-neutral-700/40 text-neutral-300 px-2 py-0.5">
                      {row.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {row.fieldChanges ? (
                      <pre className="text-xs text-neutral-400 whitespace-pre-wrap break-all m-0">
                        {JSON.stringify(row.fieldChanges, null, 0)}
                      </pre>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                    {row.reason && <div className="text-neutral-500 italic mt-1">{row.reason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          testId="audit-pagination"
        />
      )}
    </Shell>
  );
}

function Shell({ title: _title, children }: { title: string; children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-6xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
